import { useCallback, useRef } from 'react';
import Input from '../../../form/Input';
import Select from '../../../form/Select';
import { useOrderStore } from '../../../../store/useOrder';

/**
 * @author taulmax
 * @date 2024.11.06
 * @description 주문 목록 상세 페이지 - 고객 정보
 */
const ODCustomerInfo = ({
  service,
  customerName,
  setCustomerName,
  customerSolarLunar,
  setCustomerSolarLunar,
  customerSex,
  setCustomerSex,
  customerBirth,
  setCustomerBirth,
  disabled = false,
}: {
  service: 1 | 2;
  customerName: string;
  setCustomerName: React.Dispatch<React.SetStateAction<string>>;
  customerSolarLunar: string;
  setCustomerSolarLunar: React.Dispatch<React.SetStateAction<string>>;
  customerSex: string;
  setCustomerSex: React.Dispatch<React.SetStateAction<string>>;
  customerBirth: string;
  setCustomerBirth: React.Dispatch<React.SetStateAction<string>>;
  disabled?: boolean;
}) => {
  const { orderDetail } = useOrderStore();
  const onChangeCustomerName = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setCustomerName(e.target.value),
    [setCustomerName]
  );
  const onChangeCustomerSolarLunar = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) =>
      setCustomerSolarLunar(e.target.value),
    [setCustomerSolarLunar]
  );
  const onChangeCustomerSex = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => setCustomerSex(e.target.value),
    [setCustomerSex]
  );
  const onChangeBirth = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value.replace(/\D/g, ''); // 숫자만 남기기

      // yyyy.mm.dd hh:mm 형식으로 변환
      if (value.length > 4) value = `${value.slice(0, 4)}.${value.slice(4)}`;
      if (value.length > 7) value = `${value.slice(0, 7)}.${value.slice(7)}`;
      if (value.length > 10) value = `${value.slice(0, 10)} ${value.slice(10)}`;
      if (value.length > 13)
        value = `${value.slice(0, 13)}:${value.slice(13, 15)}`;

      setCustomerBirth(value);
    },
    [setCustomerBirth]
  );

  const solarRef = useRef<HTMLSelectElement>(null);
  const solarOptions = [
    { value: 'SOLAR', option: '양력' },
    { value: 'LUNAR', option: '음력' },
    { value: 'LUNAR_INTER', option: '음력(윤달)' },
  ];

  const sexRef = useRef<HTMLSelectElement>(null);
  const sexOptions = [
    { value: 'M', option: '남성' },
    { value: 'F', option: '여성' },
  ];

  if (
    service === 2 &&
    orderDetail &&
    orderDetail.contractCustInfo.length === 1
  ) {
    return (
      <div className="!flex !items-center !justify-center !h-32 !text-gray-500 !text-sm">
        개별 서비스 (1인) 상품입니다.
      </div>
    );
  }

  return (
    <div className="!space-y-3">
      {/* 이름 */}
      <div>
        <label className="!block !text-xs !text-gray-600 !mb-1">이름</label>
        {orderDetail ? (
          <Input
            value={customerName}
            onChange={onChangeCustomerName}
            width="100%"
            textAlign="left"
            placeholder="이름을 입력하세요"
            disabled={disabled}
          />
        ) : (
          <div className="!h-9 !bg-gray-100 !rounded-md !animate-pulse"></div>
        )}
      </div>

      <div className="!grid !grid-cols-2 !gap-3">
        {/* 양력/음력 */}
        <div>
          <label className="!block !text-xs !text-gray-600 !mb-1">양력/음력</label>
          {orderDetail ? (
            <Select
              selectRef={solarRef}
              value={customerSolarLunar}
              onChange={onChangeCustomerSolarLunar}
              options={solarOptions}
              style={{ width: '100%' }}
              disabled={disabled}
            />
          ) : (
            <div className="!h-9 !bg-gray-100 !rounded-md !animate-pulse"></div>
          )}
        </div>

        {/* 성별 */}
        <div>
          <label className="!block !text-xs !text-gray-600 !mb-1">성별</label>
          {orderDetail ? (
            <Select
              selectRef={sexRef}
              value={customerSex}
              onChange={onChangeCustomerSex}
              options={sexOptions}
              style={{ width: '100%' }}
              disabled={disabled}
            />
          ) : (
            <div className="!h-9 !bg-gray-100 !rounded-md !animate-pulse"></div>
          )}
        </div>
      </div>

      {/* 생년월일시 */}
      <div>
        <label className="!block !text-xs !text-gray-600 !mb-1">생년월일시</label>
        {orderDetail ? (
          <Input
            value={customerBirth}
            onChange={onChangeBirth}
            width="100%"
            textAlign="left"
            placeholder="YYYY.MM.DD HH:MM"
            disabled={disabled}
          />
        ) : (
          <div className="!h-9 !bg-gray-100 !rounded-md !animate-pulse"></div>
        )}
      </div>
    </div>
  );
};

export default ODCustomerInfo;
